import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { DisplayNamesLastFirstProvider } from '@apps/web/context/display_names_last_first';
import { getRunEnvironment } from '@apps/web/lib/runtime_config';
import {
    type useOrgMemberProfiles,
    useUserProfile,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';

type OrgMemberProfile = NonNullable<
    ReturnType<typeof useOrgMemberProfiles>['data']
>[number];

type OrganizationChangeGuard = (proceed: () => void) => boolean;

type OrganizationContextValue = {
    readonly organizationId: string | null;
    readonly setOrganizationId: (organizationId: string | null) => void;
    readonly registerChangeGuard?: (
        guard: OrganizationChangeGuard,
    ) => () => void;
    readonly memberships: ReadonlyArray<OrgMemberProfile>;
    readonly isLoading: boolean;
};

export const OrganizationContext = createContext<
    OrganizationContextValue | undefined
>(undefined);

const STORAGE_KEY_BASE = 'phoebe-organization-id';
const PHOEBE_DEVELOPMENT_ORGANIZATION_NAME = 'Phoebe Development Organization';

type StoredOrganizationIds = {
    readonly sessionOrganizationId: string | null;
    readonly localOrganizationId: string | null;
};

// Selected org is kept per-tab in sessionStorage so switching orgs in one tab
// cannot silently hijack open tabs on other orgs and trigger 404s on their
// SWR refetches. localStorage is used only as a "last-active-tab" snapshot so
// brand-new tabs open on whichever org the user was most recently looking at.
function readStoredOrganizationIds(storageKey: string): StoredOrganizationIds {
    let sessionOrganizationId: string | null = null;
    try {
        sessionOrganizationId = sessionStorage.getItem(storageKey);
    } catch {
        // Ignore storage errors (e.g., private browsing).
    }

    let localOrganizationId: string | null = null;
    try {
        localOrganizationId = localStorage.getItem(storageKey);
    } catch {
        // Ignore storage errors (e.g., private browsing).
    }

    return { sessionOrganizationId, localOrganizationId };
}

function persistSessionOrganizationId(
    storageKey: string,
    organizationId: string | null,
): void {
    try {
        if (organizationId) {
            sessionStorage.setItem(storageKey, organizationId);
        } else {
            sessionStorage.removeItem(storageKey);
        }
    } catch {
        // Ignore storage errors (e.g., private browsing).
    }
}

function persistLocalOrganizationId(
    storageKey: string,
    organizationId: string | null,
): void {
    try {
        if (organizationId) {
            localStorage.setItem(storageKey, organizationId);
        } else {
            localStorage.removeItem(storageKey);
        }
    } catch {
        // Ignore storage errors (e.g., private browsing).
    }
}

// Read the deep-link `organizationId` straight off the initial URL. The
// provider mounts above the router, so router search hooks aren't available
// yet; `window.location.search` is the synchronous source of truth at first
// paint (the same source `app_layout` uses for the Slack deep-link claim).
function readUrlOrganizationId(): string | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        return (
            new URLSearchParams(window.location.search).get('organizationId') ||
            null
        );
    } catch {
        return null;
    }
}

function getDefaultOrganizationId(
    memberships: ReadonlyArray<OrgMemberProfile>,
): string | null {
    if (getRunEnvironment() === 'development') {
        const developmentOrganization = memberships.find(
            (membership) =>
                membership.organization_name?.trim().toLowerCase() ===
                PHOEBE_DEVELOPMENT_ORGANIZATION_NAME.toLowerCase(),
        );
        if (developmentOrganization) {
            return developmentOrganization.organization_id;
        }
    }

    return memberships[0]?.organization_id ?? null;
}

function resolveOrganizationId(
    storageKey: string | null,
    memberships: ReadonlyArray<OrgMemberProfile>,
    urlOrganizationId: string | null,
    isFirstResolution: boolean,
): string | null {
    if (!storageKey || memberships.length === 0) {
        return null;
    }

    const { sessionOrganizationId, localOrganizationId } =
        readStoredOrganizationIds(storageKey);
    const isOrganizationIdValid = (
        organizationId: string | null,
    ): organizationId is string =>
        !!organizationId &&
        memberships.some(
            (membership) => membership.organization_id === organizationId,
        );

    // A document loaded on `?organizationId=` is born on that org even when
    // this tab already holds a per-tab value, because that value is not
    // necessarily a choice the user made: the provider also parks a default
    // there. A customer following an emailed setup link hits exactly that —
    // they sit on `/change-password` (no org in the URL) while the provider
    // caches the default org for the tab, and the password step then reloads
    // the same tab onto the reminded org's deep link. An implicit default must
    // never outrank an org the customer was explicitly sent to. Only the first
    // resolution of a document gets this precedence, so a later re-resolution
    // still keeps an in-tab switch over a stale URL (see below).
    if (isFirstResolution && isOrganizationIdValid(urlOrganizationId)) {
        persistSessionOrganizationId(storageKey, urlOrganizationId);
        return urlOrganizationId;
    }

    // A per-tab selection (sessionStorage) outranks the URL so an explicit
    // in-tab org switch is never clobbered by a stale `?organizationId=` left
    // in the address bar on a later re-resolution (e.g. a mode toggle re-keys
    // the profile query). On a fresh deep-link tab sessionStorage is empty, so
    // the URL org wins and is cached per-tab below — the tab is born on it.
    if (isOrganizationIdValid(sessionOrganizationId)) {
        return sessionOrganizationId;
    }

    if (sessionOrganizationId) {
        persistSessionOrganizationId(storageKey, null);
    }

    if (isOrganizationIdValid(urlOrganizationId)) {
        // Cache per-tab so the deep-link org sticks for this tab without
        // touching the cross-tab "last-active" localStorage snapshot.
        persistSessionOrganizationId(storageKey, urlOrganizationId);
        return urlOrganizationId;
    }

    if (isOrganizationIdValid(localOrganizationId)) {
        // Cache the resolved value in sessionStorage so later re-resolutions
        // in this tab (e.g., triggered by a mode switch re-keying the profile
        // query) do not fall through to localStorage, where another tab may
        // have written a different org in the meantime. That fall-through is
        // exactly the cross-tab hijack this provider is meant to prevent.
        persistSessionOrganizationId(storageKey, localOrganizationId);
        return localOrganizationId;
    }

    // Drop stale values (e.g., user lost access to that org) only from the
    // storage layer where they were found. A stale per-tab value should not
    // wipe a valid last-active-tab snapshot.
    if (localOrganizationId) {
        persistLocalOrganizationId(storageKey, null);
    }

    const fallback = getDefaultOrganizationId(memberships);
    if (fallback) {
        persistSessionOrganizationId(storageKey, fallback);
    }
    return fallback;
}

export function OrganizationProvider({ children }: { children: ReactNode }) {
    const { data, isLoading } = useUserProfile({
        reason: 'organization-provider',
    });
    const memberships = data?.org_member_profiles;
    const userId = data?.user?.id ?? null;
    const storageKey = useMemo(
        () => (userId ? `${STORAGE_KEY_BASE}:${userId}` : null),
        [userId],
    );
    // The deep-link org is the initial URL's `?organizationId=`; read it once at
    // mount (it can't change without a remount) and reuse it for init + the
    // reconciliation effect.
    const initialUrlOrganizationId = useMemo(() => readUrlOrganizationId(), []);
    // Whether this document has already resolved once. Only the first
    // resolution lets the deep-link org outrank a per-tab selection.
    const hasResolvedOrganizationRef = useRef(false);

    const [organizationId, _setOrganizationId] = useState<string | null>(() => {
        // Adopt the deep-link org at first paint so a tab opened on
        // `?organizationId=` is *born* on that org instead of resolving to the
        // last-active org and switching into it (which flashes the wrong org +
        // an auto-switch toast).
        if (!initialUrlOrganizationId) {
            return null;
        }
        // If the profile is already cached at mount, only adopt the URL org when
        // it's a real membership — otherwise it would briefly drive org-scoped
        // queries against an org the user can't access (403/404). When the
        // profile hasn't loaded yet we can't validate, so adopt optimistically
        // and let the effect below revert a non-member org once it loads.
        if (
            memberships &&
            !memberships.some(
                (membership) =>
                    membership.organization_id === initialUrlOrganizationId,
            )
        ) {
            return null;
        }
        return initialUrlOrganizationId;
    });

    useEffect(() => {
        // Mode changes re-key the profile query. Keep the current organization
        // while the new profile request is in flight so toggling modes does not
        // reset org selection.
        if (isLoading) {
            return;
        }

        if (!storageKey || !memberships) {
            _setOrganizationId(null);
            return;
        }

        const isFirstResolution = !hasResolvedOrganizationRef.current;
        hasResolvedOrganizationRef.current = true;
        _setOrganizationId(
            resolveOrganizationId(
                storageKey,
                memberships,
                initialUrlOrganizationId,
                isFirstResolution,
            ),
        );
    }, [isLoading, storageKey, memberships, initialUrlOrganizationId]);

    // Snapshot this tab's current org into localStorage whenever it becomes
    // the active tab, so newly opened tabs default to the org the user was
    // most recently looking at rather than whatever another background tab
    // happened to write last.
    useEffect(() => {
        if (!storageKey || !organizationId) {
            return;
        }
        const activeStorageKey = storageKey;
        const activeOrganizationId = organizationId;

        function snapshotToLocalStorage() {
            if (
                typeof document !== 'undefined' &&
                document.visibilityState !== 'visible'
            ) {
                return;
            }
            persistLocalOrganizationId(activeStorageKey, activeOrganizationId);
        }

        snapshotToLocalStorage();
        document.addEventListener('visibilitychange', snapshotToLocalStorage);
        window.addEventListener('focus', snapshotToLocalStorage);
        return () => {
            document.removeEventListener(
                'visibilitychange',
                snapshotToLocalStorage,
            );
            window.removeEventListener('focus', snapshotToLocalStorage);
        };
    }, [storageKey, organizationId]);

    const changeGuards = useRef(new Set<OrganizationChangeGuard>());
    const registerChangeGuard = useCallback(
        (guard: OrganizationChangeGuard) => {
            changeGuards.current.add(guard);
            return () => {
                changeGuards.current.delete(guard);
            };
        },
        [],
    );

    const setOrganizationId = useCallback(
        (next: string | null) => {
            if (next === organizationId || !storageKey) {
                return;
            }
            const orgIdToStore =
                typeof next === 'string' && next.length > 0 ? next : null;

            const proceed = () =>
                _setOrganizationId((prev) => {
                    if (prev === next) {
                        return prev;
                    }

                    if (!storageKey) {
                        return prev;
                    }

                    persistSessionOrganizationId(storageKey, orgIdToStore);
                    if (
                        orgIdToStore === null &&
                        typeof document !== 'undefined' &&
                        document.visibilityState === 'visible'
                    ) {
                        persistLocalOrganizationId(storageKey, null);
                    }
                    return next;
                });
            // Organization switches do not navigate through the router. Give
            // staged editors the same chance to protect their draft here.
            for (const guard of changeGuards.current) {
                if (guard(proceed)) {
                    return;
                }
            }
            proceed();
        },
        [storageKey, organizationId],
    );

    const value = useMemo<OrganizationContextValue>(
        () => ({
            organizationId,
            setOrganizationId,
            registerChangeGuard,
            memberships: memberships ?? [],
            isLoading,
        }),
        [
            organizationId,
            setOrganizationId,
            registerChangeGuard,
            memberships,
            isLoading,
        ],
    );

    return (
        <OrganizationContext.Provider value={value}>
            <DisplayNamesLastFirstProvider organizationId={organizationId}>
                {children}
            </DisplayNamesLastFirstProvider>
        </OrganizationContext.Provider>
    );
}

export function useOrganizationContext() {
    const context = useContext(OrganizationContext);
    if (!context) {
        throw new Error(
            'useOrganizationContext must be used within OrganizationProvider',
        );
    }
    return context;
}
